<template>
  <div class="owner-program-result" data-testid="owner-program-result">
    <div class="owner-program-result__summary">
      <bg-card class="owner-program-result__summary-card">
        <bg-text size="title-3">
          {{ formatNumber(summary.total_target) }}
        </bg-text>
        <bg-text size="body-3" class="bg-u-text-neutral-700">
          {{ targetCardLabel }}
        </bg-text>
      </bg-card>

      <bg-card class="owner-program-result__summary-card">
        <bg-text size="title-3">
          <span class="owner-program-result__joined">{{ joinedPercent }}%</span>
          ({{ formatNumber(summary.total_joined) }})
        </bg-text>
        <bg-text size="body-3" class="bg-u-text-neutral-700">
          {{ joinedLabel }}
        </bg-text>
      </bg-card>

      <bg-card class="owner-program-result__summary-card">
        <bg-text size="title-3">
          <span class="owner-program-result__not-joined">
            {{ notJoinedPercent }}%
          </span>
          ({{ formatNumber(summary.total_not_joined) }})
        </bg-text>
        <bg-text size="body-3" class="bg-u-text-neutral-700">
          {{ notJoinedLabel }}
        </bg-text>
      </bg-card>
    </div>

    <data-table
      :columns="columns"
      :rows="rows"
      :loading="isLoading"
      class="bg-u-mt-md"
    >
      <template #column-owner="{ row }">
        <bg-text size="body-2">{{ row.owner_name }}</bg-text>
        <bg-link
          variant="medium"
          target="_blank"
          rel="noopener noreferrer"
          :href="getWhatsappLink(row.owner_phone)"
        >
          {{ row.owner_phone }}
        </bg-link>
      </template>

      <template #header-first_cta="{ column }">
        <cta-header-tooltip :label="column.label" :cta="ctas[0]" />
      </template>

      <template #header-second_cta="{ column }">
        <cta-header-tooltip :label="column.label" :cta="ctas[1]" />
      </template>

      <template #column-first_cta="{ row }">
        <cta-result-cell :cta="row.first_cta" />
      </template>

      <template #column-second_cta="{ row }">
        <cta-result-cell :cta="row.second_cta" />
      </template>

      <template #empty-state>
        <bg-text size="title-5">Belum Ada Partisipan</bg-text>
        <bg-text size="body-2">
          Belum ada pemilik yang berpartisipasi pada program ini.
        </bg-text>
      </template>
    </data-table>

    <bg-pagination
      v-if="totalPage > 1"
      :value="currentPage"
      :page-total="totalPage"
      class="bg-u-mt-xl"
      @click="changePage"
    />
  </div>
</template>

<script>
import {
  ref,
  computed,
  onMounted,
  getCurrentInstance,
} from '@vue/composition-api';
import { BgCard, BgText, BgLink, BgPagination } from 'bangul-vue';

import ownerProgramApi from '@admin_endpoints/owner-program';
import { PROGRAM_TARGET_LABEL } from '@admin_pages/OwnerProgram/constants';
import { getWhatsappLink } from 'Utils/whatsapp';
import { dateFormatterToDisplay } from '@/utils/formatter';
import DataTable from '@/components/molecules/DataTable';

import CtaResultCell from './components/CtaResultCell';
import CtaHeaderTooltip from './components/CtaHeaderTooltip';

const ITEMS_PER_PAGE = 20;

const formatNumber = value => Number(value || 0).toLocaleString('id-ID');

export default {
  name: 'OwnerProgramResult',

  components: {
    BgCard,
    BgText,
    BgLink,
    BgPagination,
    DataTable,
    CtaResultCell,
    CtaHeaderTooltip,
  },

  props: {
    programId: {
      type: [Number, String],
      required: true,
    },
    targetType: {
      type: String,
      default: '',
    },
    ctaType: {
      type: String,
      default: 'single',
    },
    // Active CTA config: [{ action, label, fieldTitles }].
    ctas: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const { $error, $toast } = getCurrentInstance().proxy;

    const summary = ref({
      total_target: 0,
      total_joined: 0,
      total_not_joined: 0,
    });

    const rows = ref([]);
    const isLoading = ref(false);
    const currentPage = ref(1);
    const totalItems = ref(0);

    const totalPage = computed(() =>
      Math.ceil(totalItems.value / ITEMS_PER_PAGE)
    );

    const percentage = count => {
      if (!summary.value.total_target) return 0;

      return Math.round((count / summary.value.total_target) * 10000) / 100;
    };

    const joinedPercent = computed(() =>
      percentage(summary.value.total_joined)
    );
    const notJoinedPercent = computed(() =>
      percentage(summary.value.total_not_joined)
    );

    const isDouble = computed(() => props.ctaType === 'double');

    /*
     * "Link Dibuka" wording is used only for a single open-link CTA; every
     * other case (submit, fill_data, double CTA) reads as confirmation.
     */
    const isLinkLabel = computed(
      () => !isDouble.value && props.ctas[0]?.action === 'open_link'
    );

    const targetCardLabel = computed(
      () =>
        `Target Program (${
          PROGRAM_TARGET_LABEL[props.targetType] || props.targetType
        })`
    );

    const joinedLabel = computed(() =>
      isLinkLabel.value ? 'Link Dibuka' : 'Pemilik Konfirmasi'
    );
    const notJoinedLabel = computed(() =>
      isLinkLabel.value ? 'Link Tidak Dibuka' : 'Pemilik Tidak Konfirmasi'
    );

    const columns = computed(() => {
      const ctaColumns = isDouble.value
        ? [
            { label: 'Tombol A', key: 'first_cta', width: 120 },
            { label: 'Tombol B', key: 'second_cta', width: 120 },
          ]
        : [
            {
              label:
                props.ctas[0]?.action === 'fill_data'
                  ? 'Isian Data'
                  : 'Tombol A',
              key: 'first_cta',
            },
          ];

      return [
        { label: 'No', key: 'no', width: 60 },
        { label: 'Nama Pemilik', key: 'owner', width: 200 },
        { label: 'ID Properti', key: 'property_id' },
        { label: 'Nama Properti', key: 'property_name', width: 300 },
        { label: 'Kota', key: 'city' },
        { label: 'Kecamatan', key: 'district' },
        ...ctaColumns,
        { label: 'Update Terakhir', key: 'updated_at' },
      ];
    });

    const mapProperty = (property, index, offset) => ({
      no: offset + index + 1,
      owner_name: property.owner_name,
      owner_phone: property.owner_phone,
      property_id: property.property_id,
      property_name: property.property_name,
      city: property.city,
      district: property.district,
      first_cta: property.first_cta,
      second_cta: property.second_cta,
      updated_at: dateFormatterToDisplay(
        property.updated_at,
        'DD/MM/YYYY, HH:mm'
      ),
    });

    const fetchSummary = async () => {
      try {
        const { data } = await ownerProgramApi.getOwnerProgramResultSummary(
          props.programId
        );

        summary.value = data.summary;
      } catch (error) {
        $error?.report?.(error);
      }
    };

    const fetchResult = async () => {
      isLoading.value = true;

      try {
        const offset = (currentPage.value - 1) * ITEMS_PER_PAGE;

        const { data } = await ownerProgramApi.getOwnerProgramResult(
          props.programId,
          {
            offset,
            limit: ITEMS_PER_PAGE,
          }
        );

        totalItems.value = data?.metadata?.total || 0;
        rows.value = (data?.properties || []).map((property, index) =>
          mapProperty(property, index, offset)
        );
      } catch (error) {
        $error?.report?.(error);
        $toast.show('Gagal memuat hasil program. Silakan coba lagi.', {
          variant: 'error',
        });
      } finally {
        isLoading.value = false;
      }
    };

    const changePage = page => {
      currentPage.value = page;
      fetchResult();
    };

    onMounted(() => {
      fetchSummary();
      fetchResult();
    });

    return {
      summary,
      rows,
      isLoading,
      currentPage,
      totalPage,
      joinedPercent,
      notJoinedPercent,
      targetCardLabel,
      joinedLabel,
      notJoinedLabel,
      columns,
      changePage,
      formatNumber,
      getWhatsappLink,
    };
  },
};
</script>

<style lang="scss" scoped src="./OwnerProgramResult.scss" />
